import React, { useEffect, useState, Component } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { PlugIcon, CodeIcon, SendIcon, SearchIcon, BarChart3Icon, CalendarCheckIcon } from 'lucide-react';
// The capabilities the headline cycles through — each is something credits
// power Giga to do, paired with a lucide glyph shown in a Giga-orange rounded
// square so every item reads as a branded lockup (no third-party purple).
const CAPABILITIES: {
  label: string;
  icon: ComponentType<{
    size?: number;
    strokeWidth?: number;
    className?: string;
  }>;
}[] = [{
  label: 'Integrations',
  icon: PlugIcon
}, {
  label: 'Code',
  icon: CodeIcon
}, {
  label: 'Outreach',
  icon: SendIcon
}, {
  label: 'Research',
  icon: SearchIcon
}, {
  label: 'Reports',
  icon: BarChart3Icon
}, {
  label: 'Scheduling',
  icon: CalendarCheckIcon
}];

// The animated trailing word in the headline: "Giga does: ___". Cycles through
// CAPABILITIES on a calm timer, sliding + blurring each in/out. Width is held
// stable by an invisible sizer stack so the centered headline never reflows.
// Respects reduced-motion by rendering the first item statically.
function RotatingCapability() {
  const reduce = useReducedMotion();
  const [index, setIndex] = useState(0);
  useEffect(() => {
    if (reduce) return;
    const id = setInterval(() => {
      setIndex(i => (i + 1) % CAPABILITIES.length);
    }, 2200);
    return () => clearInterval(id);
  }, [reduce]);
  const active = CAPABILITIES[index];
  const ActiveIcon = active.icon;
  return <span className="relative inline-grid place-items-start align-baseline" data-id="element-3642" data-mp="20f7rsj21bhsm">
      {/* Invisible sizer — reserves the width of the widest capability so the
          centered headline never shifts as words swap. */}
      <span className="invisible col-start-1 row-start-1 grid" aria-hidden="true" data-id="element-3643" data-mp="1cnk92oexfd1f">
        
        {CAPABILITIES.map(c => {
        const Icon = c.icon;
        return <span key={c.label} className="col-start-1 row-start-1 inline-flex items-center gap-3 whitespace-nowrap" data-id="element-3644" data-mp="1hlu6z6s1crqq">
              
              <span className="inline-flex items-center justify-center rounded-2xl bg-[#EA580C] w-[1em] h-[1em]" data-id="element-3645" data-mp="3el2y35i87o71">
                <Icon size={1} data-id="element-3646" data-mp="39lhwenbo91qm" />
              </span>
              {c.label}
            </span>;
      })}
      </span>
      {/* Visible animated layer, absolutely overlaid on the sizer. */}
      <span className="col-start-1 row-start-1 block relative" data-id="element-3647" data-mp="65aw8qsgtmml">
        <AnimatePresence initial={false} mode="wait" data-id="element-3648" data-mp="64hte0wmnh9q">
          <motion.span key={active.label} initial={reduce ? {
          opacity: 0
        } : {
          opacity: 0,
          y: '0.35em',
          filter: 'blur(6px)'
        }} animate={{
          opacity: 1,
          y: 0,
          filter: 'blur(0px)'
        }} exit={reduce ? {
          opacity: 0
        } : {
          opacity: 0,
          y: '-0.35em',
          filter: 'blur(6px)'
        }} transition={{
          duration: 0.45,
          ease: [0.16, 1, 0.3, 1]
        }} className="inline-flex items-center gap-3 whitespace-nowrap" data-id="element-3649" data-mp="15y7g0i1a5hd">
            
            <span className="inline-flex items-center justify-center rounded-2xl bg-[#EA580C] text-white w-[1.15em] h-[1.15em] shadow-sm" data-id="element-3650" data-mp="xcsvgnf1lf7z">
              <ActiveIcon size={undefined} className="w-[0.62em] h-[0.62em]" strokeWidth={2.5} data-id="element-3651" data-mp="11dbnoikuknap" />
              
            </span>
            <span data-id="element-3652" data-mp="166e9g4jdusgc">{active.label}</span>
          </motion.span>
        </AnimatePresence>
      </span>
    </span>;
}
function GigaAvatar({
  size = 24
}: {
  size?: number;
}) {
  return <span className="flex shrink-0 items-center justify-center rounded-full bg-[#EA580C]" style={{
    width: size,
    height: size
  }} data-id="element-3653" data-mp="1g1zfcdrkocvd">
      
      <span className="bg-white" style={{
      width: size * 0.56,
      height: size * 0.56,
      WebkitMaskImage: 'url(https://cdn.magicpatterns.com/uploads/bxQBDjkPJfWx4r7DHyApcd/Vector.png)',
      maskImage: 'url(https://cdn.magicpatterns.com/uploads/bxQBDjkPJfWx4r7DHyApcd/Vector.png)',
      WebkitMaskRepeat: 'no-repeat',
      maskRepeat: 'no-repeat',
      WebkitMaskPosition: 'center',
      maskPosition: 'center',
      WebkitMaskSize: 'contain',
      maskSize: 'contain'
    }} data-id="element-3654" data-mp="1p3yxj9ebpmzw" />
      
    </span>;
}
export function PricingCreditsUseCases() {
  return <section className="py-20 md:py-32 bg-[#FAF8F4] overflow-hidden" data-id="element-3655" data-mp="35itfgkg3b8c8">
      <div className="max-w-[1280px] mx-auto px-6 md:px-10" data-id="element-3656" data-mp="30la01h7nopip">
        <div className="text-center mb-16 md:mb-20" data-id="element-3657" data-mp="2cunsrs5h799r">
          <h2 className="text-4xl md:text-6xl font-title font-bold text-[#2B211A] tracking-tight leading-[1.1]" data-id="element-3658" data-mp="2cvfl8u3s0qvt">
            <span className="block" data-id="element-3659" data-mp="27xe44epwv1mz">Credits power everything</span>
            <span className="inline-flex flex-wrap items-center justify-center gap-x-4 gap-y-2 mt-1" data-id="element-3660" data-mp="2mlipxrk9ft9z">
              Giga does:
              <RotatingCapability data-id="element-3661" data-mp="2hsfu1cs0g7qm" />
            </span>
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-3662" data-mp="1u2v8qn8dwye6">
          {/* Card 1 */}
          <div className="bg-gradient-to-b from-white to-[#FAF8F4] border border-neutral-200 rounded-[2rem] p-8 shadow-sm flex flex-col" data-id="element-3663" data-mp="1p4k0ggxqmvy2">
            <span className="text-[#EA580C] font-semibold text-sm mb-2" data-id="element-3664" data-mp="9dr6yg8tfk21">
              Quick Tasks
            </span>
            <h3 className="text-3xl font-title font-bold text-[#2B211A] mb-2" data-id="element-3665" data-mp="3rs0cmsim5upy">
              100-300 credits
            </h3>
            <p className="text-neutral-500 font-medium mb-8" data-id="element-3666" data-mp="3vudkbmgx82pd">
              Slack summary + CRM follow-up
            </p>

            <div className="flex-1 flex flex-col gap-4" data-id="element-3667" data-mp="nebqjemk1jwl">
              <div className="flex gap-3" data-id="element-3668" data-mp="sd5231ia70u4">
                <div className="w-8 h-8 rounded-full bg-[#E7DFD5] flex items-center justify-center text-[#6B5D52] font-bold text-xs shrink-0" data-id="element-3669" data-mp="sbu0n8cb6566">
                  S
                </div>
                <div data-id="element-3670" data-mp="jadqn1peo52h">
                  <div className="flex items-baseline gap-2 mb-1" data-id="element-3671" data-mp="je1k60uuorh7">
                    <span className="font-semibold text-sm text-[#2B211A]" data-id="element-3672" data-mp="2z2a02nwmr0vy">
                      Sarah
                    </span>
                    <span className="text-xs text-neutral-400" data-id="element-3673" data-mp="34035heq3zqv6">9:14 AM</span>
                  </div>
                  <div className="bg-white border border-neutral-100 rounded-2xl rounded-tl-sm p-3 text-sm text-[#2B211A] shadow-sm" data-id="element-3674" data-mp="19o1y8icefseb">
                    <span className="text-[#EA580C] font-semibold" data-id="element-3675" data-mp="zvmhtpgr3bvj">@Giga</span>{' '}
                    summarize yesterday's #sales thread and flag anything I need
                    to follow up on
                  </div>
                </div>
              </div>

              <div className="flex gap-3" data-id="element-3676" data-mp="qtdbzcwfg6oh">
                <div className="mt-1" data-id="element-3677" data-mp="lw3m5w54lt04">
                  <GigaAvatar size={32} data-id="element-3678" data-mp="lv20kxqhho45" />
                </div>
                <div data-id="element-3679" data-mp="gwiemxbw4cbs">
                  <div className="flex items-baseline gap-2 mb-1" data-id="element-3680" data-mp="gxscpmiqtkif">
                    <span className="font-semibold text-sm text-[#2B211A]" data-id="element-3681" data-mp="cwgjz84koj60">
                      Giga
                    </span>
                    <span className="text-[10px] font-bold bg-neutral-100 text-neutral-500 px-1.5 py-0.5 rounded" data-id="element-3682" data-mp="2barnn2c6hqjd">
                      APP
                    </span>
                    <span className="text-xs text-neutral-400" data-id="element-3683" data-mp="3p84e84e4c684">9:15 AM</span>
                  </div>
                  <div className="bg-[#FCF3EC] border border-[#FBEAE0] rounded-2xl rounded-tl-sm p-3 text-sm text-[#2B211A] shadow-sm" data-id="element-3684" data-mp="3u5mel6e2ld4i">
                    3 deals discussed. Two on track — Acme closes Friday, Bloom
                    signed. One needs you: DataSync went silent after the demo.
                    I drafted a follow-up in HubSpot, want me to send it?
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Card 2 */}
          <div className="bg-gradient-to-b from-white to-[#FAF8F4] border border-neutral-200 rounded-[2rem] p-8 shadow-sm flex flex-col" data-id="element-3685" data-mp="3f8n9bp6oxwd7">
            <span className="text-[#EA580C] font-semibold text-sm mb-2" data-id="element-3686" data-mp="3k9t2tp86dnns">
              Complex Workflows
            </span>
            <h3 className="text-3xl font-title font-bold text-[#2B211A] mb-2" data-id="element-3687" data-mp="ulac1o7u2ix1">
              500-1,500 credits
            </h3>
            <p className="text-neutral-500 font-medium mb-8" data-id="element-3688" data-mp="b574ub8tniet">
              Website change → ready to review
            </p>

            <div className="flex-1 flex flex-col gap-4" data-id="element-3689" data-mp="67f3kqmaejxy">
              <div className="flex gap-3" data-id="element-3690" data-mp="jxqz6zi2z0tu">
                <div className="w-8 h-8 rounded-full bg-[#D5E7E6] flex items-center justify-center text-[#526B6A] font-bold text-xs shrink-0" data-id="element-3691" data-mp="jyk8o88fc4fo">
                  M
                </div>
                <div data-id="element-3692" data-mp="tv5dotkg1fji">
                  <div className="flex items-baseline gap-2 mb-1" data-id="element-3693" data-mp="tudhphmxpfsp">
                    <span className="font-semibold text-sm text-[#2B211A]" data-id="element-3694" data-mp="oxderfldst5t">
                      Mike
                    </span>
                    <span className="text-xs text-neutral-400" data-id="element-3695" data-mp="12w38cl8zsapf">11:32 AM</span>
                  </div>
                  <div className="bg-white border border-neutral-100 rounded-2xl rounded-tl-sm p-3 text-sm text-[#2B211A] shadow-sm" data-id="element-3696" data-mp="yvjcrm42hf5e">
                    <span className="text-[#EA580C] font-semibold" data-id="element-3697" data-mp="34pt0a1vkrj5e">@Giga</span>{' '}
                    the pricing page still says "$99/mo" on the starter tier. It
                    should be "$79/mo". Can you update it?
                  </div>
                </div>
              </div>

              <div className="flex gap-3" data-id="element-3698" data-mp="2um5k5pimzra8">
                <div className="mt-1" data-id="element-3699" data-mp="2gtg0vsuht0gt">
                  <GigaAvatar size={32} data-id="element-3700" data-mp="2gsec6lkk0fgm" />
                </div>
                <div data-id="element-3701" data-mp="2lqxywo6z1va1">
                  <div className="flex items-baseline gap-2 mb-1" data-id="element-3702" data-mp="2lq6em9itrjz2">
                    <span className="font-semibold text-sm text-[#2B211A]" data-id="element-3703" data-mp="26u79n7r0hzy5">
                      Giga
                    </span>
                    <span className="text-[10px] font-bold bg-neutral-100 text-neutral-500 px-1.5 py-0.5 rounded" data-id="element-3704" data-mp="2bv4pvesgojx0">
                      APP
                    </span>
                    <span className="text-xs text-neutral-400" data-id="element-3705" data-mp="22s3jre3listc">11:38 AM</span>
                  </div>
                  <div className="bg-[#FCF3EC] border border-[#FBEAE0] rounded-2xl rounded-tl-sm p-3 text-sm text-[#2B211A] shadow-sm" data-id="element-3706" data-mp="3s7jrqq5sa8hn">
                    Done ✅ Updated the pricing page — Starter tier now shows
                    $79/mo. Preview is ready for your review:{' '}
                    <a href="#" className="text-[#EA580C] hover:underline" data-id="element-3707" data-mp="3p1m4n6raj2do">
                      acme.com/pricing-preview
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Card 3 */}
          <div className="bg-gradient-to-b from-white to-[#FAF8F4] border border-neutral-200 rounded-[2rem] p-8 shadow-sm flex flex-col md:col-span-2 lg:col-span-1" data-id="element-3708" data-mp="3f9zw5i6c7hem">
            <span className="text-[#EA580C] font-semibold text-sm mb-2" data-id="element-3709" data-mp="bu2k13om7zdn">
              Full Projects
            </span>
            <h3 className="text-3xl font-title font-bold text-[#2B211A] mb-2" data-id="element-3710" data-mp="3u0pagtf3hcmp">
              2,000+ credits
            </h3>
            <p className="text-neutral-500 font-medium mb-8" data-id="element-3711" data-mp="2widpgbwbcw6">
              Deep research + presentation
            </p>

            <div className="flex-1 flex flex-col gap-4" data-id="element-3712" data-mp="2nlfxcjmwz2z4">
              <div className="flex gap-3" data-id="element-3713" data-mp="2nitqynbretih">
                <div className="w-8 h-8 rounded-full bg-[#E7D5E4] flex items-center justify-center text-[#6B5268] font-bold text-xs shrink-0" data-id="element-3714" data-mp="2f3cva6cwjfcu">
                  L
                </div>
                <div data-id="element-3715" data-mp="2p1trbhqp6k5r">
                  <div className="flex items-baseline gap-2 mb-1" data-id="element-3716" data-mp="2p2by38azkgrx">
                    <span className="font-semibold text-sm text-[#2B211A]" data-id="element-3717" data-mp="2k2z8wgwn6xm6">
                      Lisa
                    </span>
                    <span className="text-xs text-neutral-400" data-id="element-3718" data-mp="1wcmviqimo8o7">9:14 AM</span>
                  </div>
                  <div className="bg-white border border-neutral-100 rounded-2xl rounded-tl-sm p-3 text-sm text-[#2B211A] shadow-sm" data-id="element-3719" data-mp="1rf36cvke6bhh">
                    <span className="text-[#EA580C] font-semibold" data-id="element-3720" data-mp="264irnw15lops">@Giga</span>{' '}
                    we need a competitive analysis — us vs Notion AI, Glean, and
                    Moveworks. Pricing, features, and positioning.
                  </div>
                </div>
              </div>

              <div className="flex gap-3" data-id="element-3721" data-mp="18nakdl3qpqj9">
                <div className="mt-1" data-id="element-3722" data-mp="38qbglsydueft">
                  <GigaAvatar size={32} data-id="element-3723" data-mp="38pa7hu0xg20q" />
                </div>
                <div data-id="element-3724" data-mp="3dowgtsdmwy49">
                  <div className="flex items-baseline gap-2 mb-1" data-id="element-3725" data-mp="3do4n6n3llagm">
                    <span className="font-semibold text-sm text-[#2B211A]" data-id="element-3726" data-mp="3ig4u4zi353mp">
                      Giga
                    </span>
                    <span className="text-[10px] font-bold bg-neutral-100 text-neutral-500 px-1.5 py-0.5 rounded" data-id="element-3727" data-mp="3mgex03akzdxc">
                      APP
                    </span>
                    <span className="text-xs text-neutral-400" data-id="element-3728" data-mp="2uq0pcot5uxu4">9:45 AM</span>
                  </div>
                  <div className="bg-[#FCF3EC] border border-[#FBEAE0] rounded-2xl rounded-tl-sm p-3 text-sm text-[#2B211A] shadow-sm" data-id="element-3729" data-mp="2ys5htk7vhof6">
                    I've compiled the research into a 12-slide deck. I analyzed
                    their public pricing, recent feature releases, and G2
                    reviews. Here's the draft:{' '}
                    <a href="#" className="text-[#EA580C] hover:underline font-medium" data-id="element-3730" data-mp="izg89fw731cj">
                      
                      Competitive_Analysis_Q3.pdf
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}